/**
 * Componente de instrucciones nutricionales completo
 * Basado en HowToUseNutritional de app_smoking_apr
 */
import React from "react";
import {
  Card,
  Space,
  Typography,
  Alert,
  Divider,
  Steps,
  Collapse,
  Row,
  Col,
} from "antd";
import {
  BookOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
  ExclamationCircleOutlined,
  ExperimentOutlined,
  FileTextOutlined,
  CalculatorOutlined,
  TagOutlined,
} from "@ant-design/icons";

const { Title, Paragraph } = Typography;
const { Panel } = Collapse;

const NutritionalInstructions = () => {
  return (
    <Card
      style={{
        borderRadius: 12,
        border: "1px solid #e8e8e8",
        marginTop: 24,
      }}
    >
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <BookOutlined style={{ fontSize: 48, color: "#667eea", marginBottom: 16 }} />
          <Title level={2} style={{ margin: 0, color: "#1a1a1a", fontWeight: 600 }}>
            Cómo Usar el Sistema de Etiquetado Nutricional
          </Title>
          <Paragraph type="secondary" style={{ fontSize: 16, marginTop: 12, color: "#595959" }}>
            Guía completa paso a paso para generar etiquetas nutricionales MINSAL
          </Paragraph>
        </div>

        <Alert
          message="¿Para qué sirve esto?"
          description="Este sistema te ayuda a crear las etiquetas nutricionales que van en tus productos de helado. Solo necesitas decir qué ingredientes usas y en qué cantidad, y el sistema calcula todo automáticamente. Las etiquetas cumplen con las normas del Ministerio de Salud de Chile."
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 24 }}
        />

        <Row align="middle" gutter={12} style={{ marginBottom: 16 }}>
          <Col>
            <InfoCircleOutlined style={{ fontSize: 24, color: "#667eea" }} />
          </Col>
          <Col flex={1}>
            <Title level={3} style={{ margin: 0 }}>¿Cómo funciona?</Title>
          </Col>
        </Row>
        <Paragraph style={{ fontSize: 15, lineHeight: 1.8, color: "#262626" }}>
          Imagina que quieres hacer helado de leche. Primero, le dices al sistema qué ingredientes usas (leche, azúcar, crema, etc.) 
          y cuánto de cada uno. Luego, el sistema calcula automáticamente cuántas calorías, proteínas y grasas tiene tu helado. 
          Finalmente, puedes descargar una etiqueta lista para pegar en tus productos.
        </Paragraph>

        <Divider style={{ margin: "32px 0" }} />

        <Row align="middle" gutter={12} style={{ marginBottom: 24 }}>
          <Col>
            <FileTextOutlined style={{ fontSize: 28, color: "#667eea" }} />
          </Col>
          <Col flex={1}>
            <Title level={3} style={{ margin: 0 }}>Proceso Completo (Paso a Paso)</Title>
          </Col>
        </Row>

        <Steps
          direction="vertical"
          size="default"
          items={[
            {
              icon: <ExperimentOutlined style={{ fontSize: 24, color: "#52c41a" }} />,
              title: "Paso 1: Registra tus Ingredientes",
              description: (
                <div>
                  <Paragraph strong style={{ fontSize: 15, marginTop: 12 }}>
                    ¿Qué tengo que hacer?
                  </Paragraph>
                  <Paragraph style={{ fontSize: 15 }}>
                    Ve al menú <strong>"Ingredientes Nutricionales"</strong> y registra cada ingrediente que usas para hacer tus helados.
                    Por ejemplo: leche, azúcar, crema, etc.
                  </Paragraph>
                  <Paragraph strong style={{ fontSize: 15, marginTop: 16 }}>
                    ¿Qué información necesito poner?
                  </Paragraph>
                  <ul style={{ marginLeft: 20, marginTop: 8, fontSize: 15 }}>
                    <li>
                      <strong>Nombre del ingrediente:</strong> Por ejemplo "Leche Entera", "Azúcar", "Crema de Leche"
                    </li>
                    <li>
                      <strong>Marca o proveedor:</strong> De dónde compras el ingrediente, por ejemplo "Soprole", "Colun" (esto es opcional)
                    </li>
                    <li>
                      <strong>¿En qué lo compras?</strong> Selecciona si lo compras en gramos (g), kilogramos (kg), mililitros (ml) o litros (l)
                    </li>
                    <li>
                      <strong>¿En qué sucursal lo usas?</strong> Selecciona tu sucursal
                    </li>
                    <li>
                      <strong>Información nutricional:</strong> Aquí viene lo importante. Necesitas copiar los números que vienen en la etiqueta del producto que compras:
                      <ul style={{ marginLeft: 20, marginTop: 4 }}>
                        <li>Cuántas calorías tiene</li>
                        <li>Cuántas proteínas tiene</li>
                        <li>Cuántas grasas tiene (y qué tipo de grasas)</li>
                        <li>Cuántos carbohidratos y azúcares tiene</li>
                        <li>Cuánto sodio tiene</li>
                      </ul>
                    </li>
                  </ul>
                  <Alert
                    message="💡 Consejo práctico"
                    description="Toma el envase del producto que compras (por ejemplo, el envase de leche) y busca la tabla nutricional que dice 'por 100ml' o 'por 100g'. Copia esos números exactamente como aparecen en el envase."
                    type="info"
                    icon={<InfoCircleOutlined />}
                    style={{ marginTop: 16 }}
                  />
                </div>
              ),
            },
            {
              icon: <FileTextOutlined style={{ fontSize: 24, color: "#1890ff" }} />,
              title: "Paso 2: Crea tu Receta de Helado",
              description: (
                <div>
                  <Paragraph strong style={{ fontSize: 15, marginTop: 12 }}>
                    ¿Qué tengo que hacer?
                  </Paragraph>
                  <Paragraph style={{ fontSize: 15 }}>
                    Ve al menú <strong>"Recetas"</strong> y haz clic en el botón <strong>"Nueva Receta Compleja"</strong>
                  </Paragraph>
                  <Paragraph strong style={{ fontSize: 15, marginTop: 16 }}>
                    Primero, información básica de tu receta:
                  </Paragraph>
                  <ul style={{ marginLeft: 20, marginTop: 8, fontSize: 15 }}>
                    <li>
                      <strong>¿Cómo se llama tu helado?</strong> Por ejemplo "Helado de Leche Clásico" o "Helado de Vainilla"
                    </li>
                    <li>
                      <strong>Descripción (opcional):</strong> Puedes escribir una descripción corta si quieres
                    </li>
                    <li>
                      <strong>¿Cuánto helado produce tu receta?</strong> Esto es muy importante. Si al preparar toda la receta 
                      obtienes 6.7 kilos de helado, escribe 6700 (en gramos). El sistema necesita saber cuánto produce tu receta 
                      para calcular correctamente cuántas calorías tiene cada 100 gramos de helado.
                    </li>
                    <li>
                      <strong>¿En qué sucursal preparas esta receta?</strong> Selecciona tu sucursal
                    </li>
                  </ul>
                  <Paragraph strong style={{ fontSize: 15, marginTop: 16 }}>
                    Luego, agrega los ingredientes:
                  </Paragraph>
                  <ul style={{ marginLeft: 20, marginTop: 8, fontSize: 15 }}>
                    <li>
                      Selecciona los ingredientes que registraste en el Paso 1 (por ejemplo, "Leche Entera", "Azúcar")
                    </li>
                    <li>
                      Para cada ingrediente, escribe <strong>cuánto usas</strong> y <strong>en qué unidad</strong>
                    </li>
                    <li>
                      Por ejemplo: Si usas 5 litros de leche, selecciona "Leche Entera" y escribe cantidad: 5000, unidad: ml
                    </li>
                    <li>
                      Si usas 1 kilo de azúcar, selecciona "Azúcar" y escribe cantidad: 1000, unidad: g
                    </li>
                  </ul>
                  <Alert
                    message="💡 ¿Cómo funciona el cálculo?"
                    description="El sistema suma todas las calorías, proteínas y grasas de los ingredientes que pusiste, y luego calcula cuánto de cada cosa tiene cada 100 gramos de tu helado final. Por eso es importante que digas cuánto helado produce tu receta completa."
                    type="info"
                    icon={<InfoCircleOutlined />}
                    style={{ marginTop: 16 }}
                  />
                </div>
              ),
            },
            {
              icon: <CalculatorOutlined style={{ fontSize: 24, color: "#faad14" }} />,
              title: "Paso 3: Calcula la Información Nutricional",
              description: (
                <div>
                  <Paragraph strong style={{ fontSize: 15, marginTop: 12 }}>
                    ¿Qué tengo que hacer?
                  </Paragraph>
                  <Paragraph style={{ fontSize: 15 }}>
                    Una vez que creaste tu receta con todos los ingredientes, necesitas que el sistema calcule la información nutricional:
                  </Paragraph>
                  <ul style={{ marginLeft: 20, marginTop: 8, fontSize: 15 }}>
                    <li>
                      En la lista de recetas, busca tu receta y haz clic en el botón <strong>"Calcular"</strong>
                    </li>
                    <li>
                      El sistema sumará todas las calorías, proteínas y grasas de los ingredientes que pusiste
                    </li>
                    <li>
                      Luego calculará cuánto de cada cosa tiene cada 100 gramos de tu helado
                    </li>
                    <li>
                      Guardará toda esta información en tu receta
                    </li>
                  </ul>
                  <Alert
                    message="💡 Ejemplo para entender mejor"
                    description="Imagina que usas 5 litros de leche (que tiene 61 calorías por cada 100ml) y 1 kilo de azúcar (que tiene 400 calorías por cada 100g). El sistema suma todo: 5 litros de leche = 3050 calorías, 1 kilo de azúcar = 4000 calorías. Total = 7050 calorías en toda la receta. Si tu receta produce 6.7 kilos de helado (6700 gramos), entonces cada 100 gramos de helado tiene aproximadamente 105 calorías."
                    type="info"
                    icon={<InfoCircleOutlined />}
                    style={{ marginTop: 16 }}
                  />
                </div>
              ),
            },
            {
              icon: <TagOutlined style={{ fontSize: 24, color: "#ff4d4f" }} />,
              title: "Paso 4: Obtén tu Etiqueta Nutricional",
              description: (
                <div>
                  <Paragraph strong style={{ fontSize: 15, marginTop: 12 }}>
                    ¿Qué tengo que hacer?
                  </Paragraph>
                  <Paragraph style={{ fontSize: 15 }}>
                    Una vez que el sistema calculó la información nutricional, ya puedes ver y descargar tu etiqueta:
                  </Paragraph>
                  <ul style={{ marginLeft: 20, marginTop: 8, fontSize: 15 }}>
                    <li>
                      En la lista de recetas, busca tu receta y haz clic en el botón <strong>"Ver"</strong> o <strong>"PDF"</strong>
                    </li>
                    <li>
                      Se abrirá una ventana mostrando cómo se verá tu etiqueta nutricional
                    </li>
                    <li>
                      La etiqueta muestra toda la información que necesita el Ministerio de Salud: calorías, proteínas, grasas, etc.
                    </li>
                    <li>
                      Puedes <strong>descargar la etiqueta en PDF</strong> para imprimirla y pegarla en tus productos
                    </li>
                    <li>
                      El tamaño de la etiqueta es el estándar que requiere el Ministerio de Salud (62mm de ancho × 100mm de alto)
                    </li>
                  </ul>
                </div>
              ),
            },
          ]}
        />

        <Divider style={{ margin: "32px 0" }} />

        <Row align="middle" gutter={12} style={{ marginBottom: 24 }}>
          <Col>
            <QuestionCircleOutlined style={{ fontSize: 28, color: "#667eea" }} />
          </Col>
          <Col flex={1}>
            <Title level={3} style={{ margin: 0 }}>Preguntas Frecuentes</Title>
          </Col>
        </Row>
        <Collapse size="large" style={{ marginTop: 16 }}>
          <Panel header="¿Dónde encuentro los números nutricionales de los ingredientes?" key="faq1">
            <Paragraph style={{ fontSize: 15 }}>
              Todos los productos que compras tienen una tabla nutricional en el envase. Busca esa tabla que dice 
              "por 100g" o "por 100ml" y copia esos números exactamente como aparecen.
            </Paragraph>
            <Paragraph style={{ fontSize: 15 }}>
              Por ejemplo: Si compras leche Soprole, toma el envase, busca la tabla nutricional (generalmente está 
              en la parte de atrás o al costado) y copia los números que aparecen ahí.
            </Paragraph>
          </Panel>
          <Panel header="¿Qué significa 'Rendimiento Total'?" key="faq2">
            <Paragraph style={{ fontSize: 15 }}>
              El <strong>Rendimiento Total</strong> es simplemente cuánto helado produce tu receta completa cuando la preparas.
            </Paragraph>
            <Paragraph style={{ fontSize: 15 }}>
              <strong>Ejemplo práctico:</strong> Si preparas toda tu receta y al final obtienes 6.7 kilos de helado, 
              entonces el rendimiento total es 6700 gramos (porque 1 kilo = 1000 gramos). Escribe 6700 en el campo.
            </Paragraph>
            <Paragraph style={{ fontSize: 15 }}>
              ¿Por qué es importante? Porque el sistema necesita saber cuánto helado produce tu receta para calcular 
              correctamente cuántas calorías tiene cada 100 gramos. Si produces más helado, las calorías se "reparten" 
              en más cantidad, entonces cada 100 gramos tendrá menos calorías.
            </Paragraph>
          </Panel>
          <Panel header="¿En qué unidades puedo escribir las cantidades?" key="faq3">
            <Paragraph style={{ fontSize: 15 }}>
              Puedes usar las unidades que prefieras, según cómo compres tus ingredientes:
            </Paragraph>
            <ul style={{ marginLeft: 20, fontSize: 15 }}>
              <li><strong>g</strong> = gramos (por ejemplo, 1000g de azúcar)</li>
              <li><strong>kg</strong> = kilogramos (por ejemplo, 1kg de azúcar es lo mismo que 1000g)</li>
              <li><strong>ml</strong> = mililitros (por ejemplo, 5000ml de leche)</li>
              <li><strong>l</strong> = litros (por ejemplo, 5 litros de leche es lo mismo que 5000ml)</li>
              <li><strong>unidad</strong> = si compras por unidades (por ejemplo, 10 huevos)</li>
            </ul>
            <Paragraph style={{ fontSize: 15 }}>
              No te preocupes por las conversiones, el sistema las hace automáticamente.
            </Paragraph>
          </Panel>
          <Panel header="¿Qué pasa si cambio la receta después?" key="faq4">
            <Paragraph style={{ fontSize: 15 }}>
              Si cambias algún ingrediente o la cantidad de algún ingrediente en tu receta, necesitas volver a calcular 
              la información nutricional. Simplemente haz clic en el botón <strong>"Calcular"</strong> otra vez y el 
              sistema actualizará todos los números automáticamente.
            </Paragraph>
          </Panel>
          <Panel header="¿Puedo imprimir la etiqueta directamente?" key="faq5">
            <Paragraph style={{ fontSize: 15 }}>
              Sí, cuando descargas el PDF, la etiqueta ya está en el tamaño correcto que requiere el Ministerio de Salud 
              (62mm de ancho × 100mm de alto). Puedes descargarlo e imprimirlo directamente en etiquetas adhesivas del 
              tamaño correcto.
            </Paragraph>
          </Panel>
        </Collapse>

        <Divider style={{ margin: "32px 0" }} />

        <Row align="middle" gutter={12} style={{ marginBottom: 16 }}>
          <Col>
            <ExclamationCircleOutlined style={{ fontSize: 28, color: "#faad14" }} />
          </Col>
          <Col flex={1}>
            <Title level={3} style={{ margin: 0 }}>Importante</Title>
          </Col>
        </Row>
        <Alert
          message="⚠️ Antes de generar tu etiqueta, asegúrate de:"
          description={
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 15 }}>
              <li>Haber registrado al menos un ingrediente con toda su información nutricional</li>
              <li>Haber creado tu receta y haber dicho cuánto helado produce (rendimiento total)</li>
              <li>Haber agregado los ingredientes a tu receta con sus cantidades</li>
              <li>Haber hecho clic en "Calcular" para que el sistema calcule la información nutricional</li>
              <li>Haber copiado los valores nutricionales de los ingredientes desde las etiquetas de los productos que compras</li>
            </ul>
          }
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
          style={{ marginTop: 16 }}
        />
      </Space>
    </Card>
  );
};

export default NutritionalInstructions;

